import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import { useNavigate, useParams } from 'react-router-dom';
import TransformFromPascalCase from 'components/Settings/Utilities/TransformFromPascalCase';
import useReloadFeed from 'components/Dashboard/hooks/useReloadFeed';
import useCreateResults from './useCreateResults';
import useIntervalCreateResults from './useIntervalCreateResults';
import useRateUpdatesListFocus from './useRateUpdatesListFocus';
import FreshUpdatesButton from './FreshUpdatesButton';
import GeneralList from './GeneralList';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { store } from 'components/Store';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import useGetRealTimeEvents from 'components/Dashboard/ParliamentLive/useGetRealtimeEvents';
import { useMainActionsBarFunctions } from 'components/Dashboard/SubNav/MainListActionsBar';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';

const ListControl = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { search, sectionsView } = state;
  const { selectedOptionParliamentLiveView } = sectionsView ?? {};
  const { contentFilters } = useFilters();
  const { contentResults, setContentResults, enforceReloadFeed } = useContentResults();
  const sectionName = createSectionName();
  const section = contentResults[sectionName];
  const topicIdQuery = getUrlParam('topic-id');
  const searchQuery = getUrlParam('search') || getUrlParam('or');
  const params = useParams();
  const navigate = useNavigate();
  const { page, mainSection } = params;

  const { callConditional } = useGetSectionFilters();
  const scrollCondition =
    section?.scrollPosition !== null && section?.scrollPosition >= 0 && section?.listSection === mainSection;
  const { mainSectionTitle, inboxSection } = useMainSectionParameter(params);
  const { addMetaTags } = useAddMetaTags();

  const [localPage, setLocalPage] = useState(page);
  const [localSearch, setLocalSearch] = useState(search);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [freshItemsCount, setFreshItemsCount] = useState(0);
  const [intervalRefreshTrigger, setIntervalRefreshTrigger] = useState(0);
  const [iconSpinning, setIconSpinning] = useState(false);

  const { activeUpdateState } = props;
  const { activeUpdate, setActiveUpdate } = activeUpdateState ?? {};

  const scrollRef = useRef(null);
  const scrollBarRef = props.scrollBarRef ?? scrollRef;
  const initialCall = useRef(null);
  const firstLoad = useRef(true);

  const { createResults, isLoading, setIsLoading, currentlyProcessing, setCurrentlyProcessing } = useCreateResults({
    customTitle: props?.customTitle,
    setActiveUpdate,
    activeUpdate,
  });

  const { rateUpdatesMode } = useRateUpdatesListFocus({
    sectionName,
    section,
    isLoading,
    createResults,
    scrollBarRef,
    activeUpdate,
    setActiveUpdate,
    registerLoadNextRateUpdatesPage: props.registerLoadNextRateUpdatesPage,
  });

  const { getRealTimeEvents } = useGetRealTimeEvents();
  const { restoreSelectedUpdates } = useMainActionsBarFunctions();
  initialCall.current = (source, { forceSetMarker = false } = {}) => {
    setIsLoading(true);
    if (enforceReloadFeed) {
      navigate(0);
    }
    setCurrentlyProcessing({ attempt: 0, state: true });
    //AE:Review if call conditional is necessary
    if (callConditional) {
      let title = `${mainSectionTitle}${TransformFromPascalCase(sectionName)} - PolicyMogul`;
      addMetaTags({ title, hash: window.location.hash });
      if (!scrollCondition || forceSetMarker) {
        dispatchSearch(source, { forceSetMarker, clearScrollPosition: forceSetMarker && scrollCondition });
      } else {
        setIsLoading(false);
        //AE: Necessary to have a setTimeOut to update the scrollPosition and allow continue using the list correctly. (Without this if you clear the search or change the topic it will not work.)
        setTimeout(() => {
          let sectionWithoutScroll = { ...section, scrollPosition: null };
          const nextContentResults = { ...contentResults, [sectionName]: sectionWithoutScroll };
          setContentResults(nextContentResults);
        }, 300);
      }
    }
  };

  const dispatchSearch = (source, { forceSetMarker = false, clearScrollPosition = false } = {}) => {
    try {
      const loadResults = async () => {
        let setMarker =
          forceSetMarker || isFirstLoad || JSON.stringify(search) !== JSON.stringify(localSearch) || page !== localPage;
        const localRealTimeEvents = await getRealTimeEvents({ source });
        await createResults({ pageNumber: null, setMarker, source, localRealTimeEvents, clearScrollPosition });
        if (forceSetMarker && scrollBarRef.current?.scrollTop) {
          scrollBarRef.current.scrollTop(0);
        }
      };
      loadResults();
    } catch (error) {}
  };

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      setIsFirstLoad(false);
    }
    restoreSelectedUpdates();
  }, []);

  useEffect(() => {
    setFreshItemsCount(0);
    setIconSpinning(false);
  }, [sectionName]);

  useLayoutEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    firstLoad.current = false;
    setLocalPage(page);
    setLocalSearch(search);
    return () => {
      source.abort(`${TransformFromPascalCase(sectionName)} canceled by the user.'`);
    };
  }, [search, topicIdQuery, searchQuery, page, contentFilters, selectedOptionParliamentLiveView]);

  //SCROLL RESTORATION
  useEffect(() => {
    if (!isLoading && scrollCondition && scrollBarRef.current && !rateUpdatesMode) {
      scrollBarRef.current.scrollTop(section.scrollPosition);
    }
  }, [isLoading, scrollCondition, section?.scrollPosition, rateUpdatesMode]);

  useReloadFeed({ createResults, currentlyProcessing });
  useIntervalCreateResults({
    callCondition: callConditional,
    currentlyProcessing,
    contentFilters,
    setFreshItemsCount,
    refreshTrigger: intervalRefreshTrigger,
    isLoading,
  });

  const elements = section?.hits ?? [];

  return (
    <div className='position-relative'>
      <FreshUpdatesButton
        freshItemsCount={freshItemsCount}
        iconSpinning={iconSpinning}
        inboxSection={inboxSection}
        navigate={navigate}
        initialCallRef={initialCall}
        setIconSpinning={setIconSpinning}
        setFreshItemsCount={setFreshItemsCount}
        setIntervalRefreshTrigger={setIntervalRefreshTrigger}
      />
      <GeneralList
        isLoading={isLoading}
        createResults={createResults}
        elements={elements}
        section={section}
        scrollBarRef={scrollBarRef}
        currentlyProcessing={currentlyProcessing}
        activeUpdateState={activeUpdateState}
      />
    </div>
  );
};

export default ListControl;
